import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import {
  QrCode,
  Camera,
  Download,
  Sparkles,
  Zap,
  Users,
  ArrowRight,
  CheckCircle2,
  Instagram,
  Star,
  Lock,
  Radio,
  Quote,
} from "lucide-react";
import Navbar from "@/components/Navbar";

const heroImg =
  "https://images.unsplash.com/photo-1502872364588-894d7d6ddfab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwYXJ0eSUyMGNyb3dkJTIwbmVvbiUyMGxpZ2h0c3xlbnwwfHx8fDE3ODU1OTE3NDZ8MA&ixlib=rb-4.1.0&q=85";
const galleryImg =
  "https://images.unsplash.com/photo-1581417478175-a9ef18f210c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxwYXJ0eSUyMGNyb3dkJTIwbmVvbiUyMGxpZ2h0c3xlbnwwfHx8fDE3ODU1OTE3NDZ8MA&ixlib=rb-4.1.0&q=85";
const vipImg =
  "https://images.pexels.com/photos/9519078/pexels-photo-9519078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const guestImg =
  "https://images.unsplash.com/photo-1527618798212-3bc916c4e9c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxzbWFydHBob25lJTIwdGFraW5nJTIwcGhvdG8lMjBuZW9uJTIwZXZlbnR8ZW58MHx8fHwxNzg1NTkxNzYyfDA&ixlib=rb-4.1.0&q=85";

export default function LandingPage() {
  const [liveEvents, setLiveEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });

  useEffect(() => {
    api.get("/public/live-events").then((r) => setLiveEvents(r.data)).catch(() => {});
    api.get("/public/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const sendContact = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`[Festagram] Contato de ${contactForm.name}`);
    const body = encodeURIComponent(
      `Nome: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMensagem:\n${contactForm.message}`,
    );
    window.location.href = `mailto:evaacriativa@gmail.com?subject=${subject}&body=${body}`;
    setShowContact(false);
    setContactForm({ name: "", email: "", message: "" });
  };

  return (
    <div className="min-h-screen text-white grain">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Evento com luzes neon"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />
          <div className="absolute inset-0 grid-lines opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] mb-8"
                data-testid="hero-badge"
              >
                <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-ring" />
                Ao vivo · Fotos em tempo real
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]"
              >
                Seu evento em fotos,
                <br />
                <span className="text-[#FF007F] text-glow-pink">com sua marca</span>{" "}
                em cada clique.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed"
              >
                Crie uma galeria colaborativa, gere um QR code, e deixe seus
                convidados enviarem fotos com a moldura do evento —
                aparecem ao vivo na tela e ficam disponíveis pra download em ZIP.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <Link to="/register" data-testid="hero-cta-primary" className="btn-neon">
                  Criar meu evento <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/vitrine-vip" data-testid="hero-cta-secondary" className="btn-cyan">
                  Ver eventos ao vivo
                </Link>
              </motion.div>

              <div className="mt-10 flex flex-wrap gap-6 text-sm text-white/60">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14]" /> Sem app pro convidado
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14]" /> Pagamento PIX
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14]" /> Download em ZIP
                </span>
              </div>
            </div>

            {/* Floating QR mock */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="lg:col-span-4"
            >
              <div className="glass rounded-3xl p-6 max-w-sm ml-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50">
                      Festa das Leoninas
                    </span>
                    <span className="text-[10px] tracking-widest text-white/40 mt-0.5">
                      02/08/2026
                    </span>
                  </div>
                  <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-ring" />
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    { src: "https://customer-assets-eiarnc6j.emergentagent.net/job_pic-frame-live/artifacts/5rzz24rk_001_Vanessa.jpg", label: "Vanessa" },
                    { src: "https://customer-assets-eiarnc6j.emergentagent.net/job_pic-frame-live/artifacts/3mj39mr9_006_Tati.jpg", label: "Tati" },
                    { src: "https://customer-assets-eiarnc6j.emergentagent.net/job_pic-frame-live/artifacts/q8suuye2_047_Amanda.jpg", label: "Amanda" },
                    { src: "https://customer-assets-eiarnc6j.emergentagent.net/job_pic-frame-live/artifacts/vl0zx0p1_062_JULIANA%20CORR%C3%8AA.jpg", label: "Juliana" },
                    { src: "https://customer-assets-eiarnc6j.emergentagent.net/job_pic-frame-live/artifacts/5rzz24rk_001_Vanessa.jpg", label: "Leoninas" },
                    { src: "https://customer-assets-eiarnc6j.emergentagent.net/job_pic-frame-live/artifacts/q8suuye2_047_Amanda.jpg", label: "Convidadas" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-lg overflow-hidden relative border border-white/10 group"
                    >
                      <img
                        src={item.src}
                        alt={item.label}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Overlay frame corners suggesting a photo frame */}
                      <div className="absolute inset-0 pointer-events-none border-2 border-[#FF007F]/40 rounded-lg" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                        <div className="text-[8px] uppercase tracking-widest text-white/80 truncate">
                          {item.label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-white/60">
                  <span className="text-[#00F0FF] font-semibold">76 fotos</span>{" "}
                  na galeria do evento
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-y border-white/10 py-4 overflow-hidden bg-[#0B0914]">
          <div className="marquee-track text-sm uppercase tracking-[0.3em] text-white/40">
            {Array.from({ length: 2 }).map((_, idx) => (
              <div key={idx} className="flex gap-16 pr-16">
                <span>Fotos ao vivo</span>
                <span className="text-[#FF007F]">•</span>
                <span>Moldura personalizada</span>
                <span className="text-[#00F0FF]">•</span>
                <span>QR code exclusivo</span>
                <span className="text-[#39FF14]">•</span>
                <span>Espaço VIP para influenciadores</span>
                <span className="text-[#FF007F]">•</span>
                <span>Download em ZIP</span>
                <span className="text-[#00F0FF]">•</span>
                <span>Pagamento PIX</span>
                <span className="text-[#39FF14]">•</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
              — Apenas 3 passos. Super fácil
            </span>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-4">
              Do QR code à foto ao vivo em
              <span className="text-[#FF007F]"> segundos.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: QrCode,
                num: "01",
                title: "Convidado escaneia o QR",
                desc: "Abre no navegador do celular. Sem app. Sem cadastro. Só o nome.",
                color: "#FF007F",
              },
              {
                icon: Camera,
                num: "02",
                title: "Tira a foto com moldura",
                desc: "A moldura do evento é aplicada automaticamente antes do envio.",
                color: "#00F0FF",
              },
              {
                icon: Zap,
                num: "03",
                title: "Aparece na galeria ao vivo",
                desc: "Novas fotos surgem em tempo real no telão e no celular de todos.",
                color: "#39FF14",
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass rounded-2xl p-8 hover-lift"
                data-testid={`step-card-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{
                      background: `${step.color}20`,
                      border: `1px solid ${step.color}60`,
                    }}
                  >
                    <step.icon className="w-6 h-6" style={{ color: step.color }} />
                  </div>
                  <span className="font-display font-black text-4xl text-white/10">
                    {step.num}
                  </span>
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight mb-2">
                  {step.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-6 gap-6">
            <div className="lg:col-span-4 glass rounded-3xl overflow-hidden group">
              <img
                src={galleryImg}
                alt="Galeria ao vivo"
                className="w-full h-64 lg:h-80 object-cover opacity-70 group-hover:opacity-90 transition-opacity"
              />
              <div className="p-8">
                <span className="text-xs uppercase tracking-[0.2em] text-[#FF007F]">
                  Galeria ao vivo
                </span>
                <h3 className="font-display font-black text-2xl sm:text-3xl mt-3 tracking-tight">
                  Fotos aparecendo em tempo real no telão da festa.
                </h3>
                <p className="text-white/60 mt-3">
                  Grid dinâmico com atualização automática. Cada convidado vira
                  fotógrafo do seu evento.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 glass rounded-3xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-full bg-[#00F0FF]/10 flex items-center justify-center border border-[#00F0FF]/40 mb-6">
                  <Download className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight">
                  Baixe tudo em um ZIP
                </h3>
                <p className="text-white/60 text-sm mt-2">
                  Ao fim do evento, um clique baixa todas as fotos originais em
                  alta resolução.
                </p>
              </div>
              <div className="mt-6 text-xs text-white/40 uppercase tracking-[0.2em]">
                Alta resolução
              </div>
            </div>

            <div className="lg:col-span-3 glass rounded-3xl p-8 flex flex-col justify-between hover-lift">
              <div>
                <div className="w-12 h-12 rounded-full bg-[#FF007F]/10 flex items-center justify-center border border-[#FF007F]/40 mb-6">
                  <Sparkles className="w-5 h-5 text-[#FF007F]" />
                </div>
                <h3 className="font-display font-bold text-2xl tracking-tight">
                  Moldura personalizada
                </h3>
                <p className="text-white/60 text-sm mt-2">
                  Suba um PNG com transparência e a moldura vira o filtro
                  oficial do seu evento — todas as fotos com sua marca.
                </p>
              </div>
            </div>

            <div className="lg:col-span-3 glass rounded-3xl p-8 flex flex-col justify-between hover-lift">
              <div>
                <div className="w-12 h-12 rounded-full bg-[#39FF14]/10 flex items-center justify-center border border-[#39FF14]/40 mb-6">
                  <Users className="w-5 h-5 text-[#39FF14]" />
                </div>
                <h3 className="font-display font-bold text-2xl tracking-tight">
                  Limite por convidado
                </h3>
                <p className="text-white/60 text-sm mt-2">
                  Configure quantas fotos cada convidado pode enviar. Evita
                  spam e mantém a curadoria em ordem.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="preco" className="relative py-24 lg:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
              — Escolha seu plano
            </span>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-4">
              Do <span className="text-[#00F0FF]">simples</span> ao{" "}
              <span className="text-[#FF007F] text-glow-pink">completo</span>
            </h2>
            <p className="text-white/60 mt-3">Pagamento único via PIX · sem mensalidade</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* BÁSICO */}
            <div
              className="glass rounded-3xl p-8 relative overflow-hidden hover-lift"
              data-testid="pricing-basic"
            >
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#00F0FF]/15 blur-3xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] font-bold mb-3">
                  Básico
                </div>
                <h3 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
                  R$ 49<span className="text-[#00F0FF]">,90</span>
                </h3>
                <p className="text-white/50 text-sm mt-1">pagamento único · por evento</p>
                <ul className="mt-6 space-y-2.5 text-sm text-white/80">
                  {[
                    "1 moldura personalizada",
                    "Até 300 fotos no evento",
                    "QR code exclusivo + adesivos PDF",
                    "Feed ao vivo · reações · legendas",
                    "Hall da Fama + Modo Telão",
                    "Download em ZIP ao final",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid="pricing-basic-cta"
                  className="btn-cyan w-full mt-8"
                >
                  Começar com o Básico <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* PREMIUM */}
            <div
              className="glass rounded-3xl p-8 relative overflow-hidden hover-lift border border-[#FF007F]/40"
              style={{ boxShadow: "0 0 40px rgba(255,0,127,0.15)" }}
              data-testid="pricing-premium"
            >
              <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-[#FF007F]/25 blur-3xl" />
              <div className="absolute top-6 right-6">
                <span className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-[#FF007F] text-white font-bold">
                  Mais completo
                </span>
              </div>
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF007F] font-bold mb-3">
                  Premium
                </div>
                <h3 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
                  R$ 99<span className="text-[#FF007F]">,90</span>
                </h3>
                <p className="text-white/50 text-sm mt-1">pagamento único · por evento</p>
                <ul className="mt-6 space-y-2.5 text-sm text-white/90">
                  {[
                    "3 molduras (convidado escolhe)",
                    "Fotos ilimitadas no evento",
                    "QR code + adesivos PDF",
                    "Feed ao vivo · reações · legendas",
                    "Hall da Fama + Modo Telão",
                    "Download em ZIP + tudo do Básico",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#FF007F] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid="pricing-premium-cta"
                  className="btn-neon w-full mt-8"
                >
                  Ir de Premium <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-white/40 mt-8">
            Aprovação do PIX em até algumas horas · sem mensalidade · sem
            surpresas
          </p>
        </div>
      </section>

      {/* LIVE EVENTS — social proof (no gallery links) */}
      <section className="relative py-20 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-4 text-xs uppercase tracking-[0.2em]">
                <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-ring" />
                Rolando agora
              </div>
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter">
                Festas <span className="text-[#39FF14]">ao vivo</span> nesse
                minuto.
              </h2>
              <p className="text-white/60 mt-4 text-lg">
                Só uma prévia do que está acontecendo. As galerias são
                privadas — quem tem QR code entra.
              </p>
            </div>
            <Link to="/vitrine-vip" data-testid="see-all-live" className="btn-cyan">
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {liveEvents.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center max-w-xl mx-auto">
              <Radio className="w-8 h-8 text-white/20 mx-auto mb-3" />
              <p className="text-white/60">
                Nenhuma festa ao vivo agora. Seja o próximo!
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveEvents.slice(0, 6).map((ev, i) => (
                <motion.div
                  key={ev.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="glass rounded-2xl p-5 hover-lift relative overflow-hidden"
                  data-testid={`landing-live-${ev.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] pulse-ring" />
                      ao vivo
                    </span>
                    {ev.is_vip && (
                      <span className="badge-vip">
                        <Sparkles className="w-3 h-3" /> VIP
                      </span>
                    )}
                  </div>
                  <h3 className="font-display font-bold tracking-tight truncate">
                    {ev.name}
                  </h3>
                  <p className="text-xs text-white/50 mt-1">
                    por {ev.owner_name || "Anfitrião"}
                  </p>
                  <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-white/60">
                      <Camera className="w-3.5 h-3.5 text-[#00F0FF]" />
                      <span className="font-bold text-white">
                        {ev.photos_count}
                      </span>{" "}
                      fotos
                    </span>
                    <span className="flex items-center gap-1 text-white/30">
                      <Lock className="w-3 h-3" /> privada
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="relative py-20 lg:py-24 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.2em] text-[#FF007F]">
                — Depoimentos
              </span>
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-4">
                Quem já usou{" "}
                <span className="text-[#FFD700] text-glow-pink">recomenda</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((t, i) => (
                <motion.article
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="glass rounded-2xl p-6 relative"
                  data-testid={`testimonial-card-${t.id}`}
                >
                  <Quote className="w-8 h-8 text-[#FF007F]/30 mb-3" />
                  <p className="text-white/85 text-sm leading-relaxed">
                    {t.message}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <div>
                      <div className="font-bold text-sm">{t.author_name}</div>
                      <div className="text-xs text-white/50">
                        {t.event_name}
                      </div>
                    </div>
                    <div className="flex">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <Star
                          key={j}
                          className={`w-3.5 h-3.5 ${
                            j < t.rating
                              ? "fill-[#FFD700] text-[#FFD700]"
                              : "text-white/20"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VIP INVITE — smaller CTA */}
      <section className="relative py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="glass rounded-3xl p-8 lg:p-10 flex flex-wrap gap-6 items-center relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FF007F]/15 blur-3xl" />
            <div className="flex-1 min-w-[240px] relative">
              <span className="badge-vip mb-3">
                <Sparkles className="w-3 h-3" /> Espaço VIP
              </span>
              <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter">
                É influenciador?{" "}
                <span className="text-[#00F0FF]">Use grátis.</span>
              </h2>
              <p className="text-white/60 mt-2 text-sm">
                Perfis selecionados usam a Festagram sem custo. Cada foto que
                gera é divulgação orgânica.
              </p>
            </div>
            <Link to="/vip-apply" data-testid="vip-cta-apply" className="btn-neon relative">
              <Instagram className="w-4 h-4" /> Quero ser VIP
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <img
            src={guestImg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20 -z-10"
          />
          <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter">
            Bora fazer seu evento{" "}
            <span className="text-[#FF007F] text-glow-pink">brilhar</span>?
          </h2>
          <p className="text-white/60 mt-6 max-w-xl mx-auto">
            Setup em 2 minutos. Convidados escaneiam e mandam foto. Você baixa
            tudo no fim. Simples.
          </p>
          <div className="mt-10">
            <Link to="/register" data-testid="final-cta" className="btn-neon">
              Começar agora <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <div className="text-white/40">
            © {new Date().getFullYear()} Festagram — desenvolvido por{" "}
            <a
              href="https://instagram.com/evaacriativa"
              target="_blank"
              rel="noreferrer"
              className="text-[#FF007F] hover:text-[#00F0FF] transition-colors"
            >
              @evaacriativa
            </a>
          </div>
          <button
            onClick={() => setShowContact(true)}
            className="text-[#00F0FF] hover:text-white transition-colors flex items-center gap-1.5"
            data-testid="open-contact-btn"
          >
            Fale conosco → evaacriativa@gmail.com
          </button>
        </div>
      </footer>

      {/* Floating contact button */}
      <button
        onClick={() => setShowContact(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#FF007F] text-white flex items-center justify-center shadow-[0_0_24px_rgba(255,0,127,0.6)] hover:scale-110 transition-transform"
        data-testid="floating-contact-btn"
        title="Fale conosco"
        aria-label="Abrir formulário de contato"
      >
        <Quote className="w-5 h-5" />
      </button>

      {/* CONTACT MODAL */}
      {showContact && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowContact(false)}
          data-testid="contact-modal"
        >
          <div
            className="glass rounded-3xl p-8 w-full max-w-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowContact(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-white/10 transition-colors flex items-center justify-center"
              data-testid="contact-close"
              aria-label="Fechar"
            >
              ✕
            </button>
            <div className="mb-6">
              <span className="text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
                — Fale conosco
              </span>
              <h2 className="font-display font-black text-2xl tracking-tighter mt-2">
                Alguma dúvida ou sugestão?
              </h2>
              <p className="text-white/60 text-sm mt-2">
                Preenche que vou responder no{" "}
                <span className="text-[#FF007F]">evaacriativa@gmail.com</span>{" "}
                assim que possível 💜
              </p>
            </div>
            <form onSubmit={sendContact} className="space-y-4" data-testid="contact-form">
              <input
                required
                placeholder="Seu nome"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                className="input-dark"
                data-testid="contact-name"
              />
              <input
                required
                type="email"
                placeholder="Seu email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                className="input-dark"
                data-testid="contact-email"
              />
              <textarea
                required
                rows={4}
                placeholder="Sua mensagem"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="input-dark resize-none"
                data-testid="contact-message"
              />
              <button
                type="submit"
                className="btn-neon w-full"
                data-testid="contact-submit"
              >
                Enviar mensagem <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-[10px] text-white/40 text-center">
                Ao enviar, o seu app de email abre com a mensagem já preenchida
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
